import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftOutlined, CopyOutlined, ExportOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import { useDeploymentSimulation } from '../../hooks/useDeploymentSimulation';
import { WordPressConfig } from '../../types/wordpress.type';
import DeploymentHeader from './components/DeploymentHeader';
import HealthStatusBadge from './components/HealthStatusBadge';
import DeploymentStages from './components/DeploymentStages';
import EventStream from './components/EventStream';

const WordPressDeployment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = location.state?.config as WordPressConfig;
  const [showCredentials, setShowCredentials] = useState(false);

  useEffect(() => {
    if (!config) {
      navigate('/resources');
    }
  }, [config, navigate]);

  const { status, elapsedTime, cancelDeployment } = useDeploymentSimulation(config);

  useEffect(() => {
    if (status.status === 'completed') {
      setShowCredentials(true);
    }
  }, [status.status]);

  if (!config) {
    return null;
  }

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to terminate this deployment? This action cannot be undone.')) {
      cancelDeployment();
      setTimeout(() => {
        navigate('/resources');
      }, 1000);
    }
  };

  const siteUrl = `https://${config.domain}`;
  const adminUrl = `${siteUrl}/wp-admin`;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    alert(`${label} copied to clipboard!`);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top Navigation Bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-[1600px] mx-auto px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/resources')}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeftOutlined />
                <span className="text-sm font-medium">Resources</span>
              </button>
              <div className="h-4 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{config.name}</h1>
                <p className="text-xs text-gray-500">{config.domain}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors flex items-center gap-2"
                title="Configuration"
              >
                <SettingOutlined />
                Configuration
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-8 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Main Content (8 cols) */}
          <div className="col-span-8 space-y-6">
            {/* Progress Card */}
            <DeploymentHeader
              siteName={config.name}
              domain={config.domain}
              progress={status.progress}
              currentStep={status.currentStep}
              totalSteps={status.totalSteps}
              elapsedTime={elapsedTime}
              estimatedTime={status.estimatedTime}
              status={status.status}
            />

            {/* Health Status */}
            <HealthStatusBadge
              status={status.health}
              message={status.healthMessage}
              lastChecked={new Date()}
            />

            {/* Success Panel - When Completed */}
            {status.status === 'completed' && showCredentials && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm">
                <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xl">✓</span>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        Environment launched successfully
                      </h3>
                      <p className="text-sm text-gray-600 mt-0.5">
                        Your WordPress application is now running and accessible
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-5">
                  {/* Environment URL */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                      Environment URL
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2.5 font-mono text-sm text-blue-600 dark:text-blue-400">
                        {siteUrl}
                      </div>
                      <button
                        onClick={() => copyToClipboard(siteUrl, 'URL')}
                        className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        title="Copy URL"
                      >
                        <CopyOutlined className="text-gray-600" />
                      </button>
                      <button
                        onClick={() => window.open(siteUrl, '_blank')}
                        className="px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <ExportOutlined />
                        Visit Application
                      </button>
                    </div>
                  </div>

                  {/* Admin URL */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                      WordPress Admin Panel
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2.5 font-mono text-sm text-blue-600 dark:text-blue-400">
                        {adminUrl}
                      </div>
                      <button
                        onClick={() => copyToClipboard(adminUrl, 'Admin URL')}
                        className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        title="Copy Admin URL"
                      >
                        <CopyOutlined className="text-gray-600" />
                      </button>
                      <button
                        onClick={() => window.open(adminUrl, '_blank')}
                        className="px-4 py-2.5 bg-dark-bg text-white text-sm font-medium rounded-md hover:bg-gray-900 transition-colors flex items-center gap-2"
                      >
                        <ExportOutlined />
                        Admin Dashboard
                      </button>
                    </div>
                  </div>

                  {/* Credentials Section */}
                  <div className="pt-5 border-t border-gray-200">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
                      Authentication Credentials
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="block text-xs text-gray-600 mb-1.5">Username</span>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 rounded-md text-sm font-mono">
                            {config.adminUsername}
                          </code>
                          <button
                            onClick={() => copyToClipboard(config.adminUsername, 'Username')}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                            title="Copy username"
                          >
                            <CopyOutlined className="text-gray-600" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <span className="block text-xs text-gray-600 mb-1.5">Password</span>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 rounded-md text-sm font-mono">
                            {'•'.repeat(config.adminPassword.length)}
                          </code>
                          <button
                            onClick={() => copyToClipboard(config.adminPassword, 'Password')}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                            title="Copy password"
                          >
                            <CopyOutlined className="text-gray-600" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Warning */}
                    <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <span className="text-yellow-700">⚠️</span>
                        </div>
                        <div className="ml-3">
                          <p className="text-xs text-yellow-800">
                            <strong className="font-semibold">Important:</strong> Store these credentials securely.
                            They will not be displayed again after you leave this page.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Configuration Summary */}
                  <div className="pt-5 border-t border-gray-200">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
                      Configuration Summary
                    </label>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-md">
                        <span className="block text-xs text-gray-500 mb-1">Instance Type</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{config.instanceType}</span>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-md">
                        <span className="block text-xs text-gray-500 mb-1">PHP Version</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{config.phpVersion.replace('php-', '')}</span>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-md">
                        <span className="block text-xs text-gray-500 mb-1">WordPress</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{config.wpVersion.replace('wp-', '')}</span>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-md">
                        <span className="block text-xs text-gray-500 mb-1">SSL Certificate</span>
                        <span className="text-sm font-semibold text-green-600">✓ Enabled</span>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-md">
                        <span className="block text-xs text-gray-500 mb-1">CDN</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {config.enableCDN ? '✓ Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-md">
                        <span className="block text-xs text-gray-500 mb-1">Environment</span>
                        <span className="text-sm font-semibold text-gray-900 capitalize">{config.environment}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Deployment Stages */}
            <DeploymentStages stages={status.stages} />

            {/* Action Bar */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  {status.status === 'deploying' && (
                    <>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span>Deployment in progress. You can safely navigate away from this page.</span>
                    </>
                  )}
                  {status.status === 'completed' && (
                    <>
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>Environment is healthy and running</span>
                    </>
                  )}
                  {status.status === 'failed' && (
                    <>
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span>Deployment failed</span>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {status.status === 'deploying' && (
                    <button
                      onClick={handleCancel}
                      className="px-5 py-2 border border-red-300 text-red-700 rounded-md text-sm font-medium hover:bg-red-50 transition-colors"
                    >
                      Terminate
                    </button>
                  )}
                  {status.status === 'completed' && (
                    <>
                      <button
                        onClick={() => window.location.reload()}
                        className="px-5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                      >
                        <ReloadOutlined />
                        Rebuild Environment
                      </button>
                      <button
                        onClick={() => navigate('/wordpress/environments')}
                        className="px-5 py-2 bg-gray-900 text-white rounded-md text-sm font-medium hover:bg-dark-bg transition-colors"
                      >
                        View All Environments
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Event Stream (4 cols) */}
          <div className="col-span-4">
            <EventStream events={status.events} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WordPressDeployment;